"use client";

import type { ChangeEvent } from "react";
import type { MotionValue } from "motion/react";
import type { Segment, TranscriptionResponse } from "@ui/components/demos/STT/types";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { transcribeSttFile } from "@/app/_api/transcribeSttFile";
import { transcribeSttUrl, type SttUrlTranscribeResult } from "@/app/_api/transcribeSttUrl";
import { captureEvent } from "@/app/_analytics/analytics";
import { convertSampleResponseToSegments, MEDIA_CONTENT_TYPES } from "@ui/components/demos/STT/utils";
import { EventName } from "@mw/shared/analyticsEventNames";
import type { Media } from "@mw/shared/payload-types";
import { useSTTHeroAudioControls } from "./useSTTHeroAudioControls";
import { useSTTHeroSampleSegments } from "./useSTTHeroSampleSegments";
import { useSTTHeroUpload } from "./useSTTHeroUpload";
import { getSttHeroContentAnalyticsProps } from "../helpers/sttHeroPlaybackUtils";
import type { UploadedMediaKind } from "../helpers/sttHeroPlaybackUtils";

export { getSttHeroContentAnalyticsProps } from "../helpers/sttHeroPlaybackUtils";

export type UploadFn = (file: File) => Promise<TranscriptionResponse>;

export type UrlTranscribeFn = (url: string) => Promise<SttUrlTranscribeResult>;

export type STTHeroDisplayMode = "default" | "videoPreview";

export type UseSTTHeroPlaybackOptions = {
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  uploadFn?: UploadFn;
  urlTranscribeFn?: UrlTranscribeFn;
};

export const defaultUploadFn: UploadFn = async file => {
  const [error, result] = await transcribeSttFile({ file, diarize: true });
  if (error || !result) throw new Error(error ?? "transcribe_failed");
  return result;
};

export const defaultUrlTranscribeFn: UrlTranscribeFn = async url => {
  const [error, result] = await transcribeSttUrl({ url });
  if (error || !result) throw new Error(error ?? "transcribe_failed");
  return result;
};

export interface STTHeroSample {
  id: string;
  name: string;
  audioUrl: string;
  sttResponse?: TranscriptionResponse | string;
  segments?: Segment[];
  thumbnail?: Media | null;
}

export interface STTHeroPlaybackState {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  segments: Segment[];
  isPlaying: boolean;
  isLoading: boolean;
  isUploading: boolean;
  uploadFileName: string | null;
  uploadedTranscription: TranscriptionResponse | null;
  uploadedThumbnailUrl: string | null;
  currentTime: MotionValue<number>;
  togglePlay: () => void;
  stop: () => void;
  seekToTime: (time: number) => void;
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleUrlSubmit: (url: string) => Promise<void>;
  handleDownload: () => void;
  reportTranscriptionDownload: (download_format: string) => void;
  showUploadQuotaExceeded: boolean;
  uploadedMediaKind: UploadedMediaKind | null;
}

/**
 * Public facade for the STT hero demo. It keeps the component API stable while
 * composing sample transcript loading, user uploads, and native audio playback.
 */
export function useSTTHeroPlayback(
  samples: STTHeroSample[],
  {
    selectedIndex: controlledSelectedIndex,
    onSelectedIndexChange,
    uploadFn = defaultUploadFn,
    urlTranscribeFn = defaultUrlTranscribeFn,
  }: UseSTTHeroPlaybackOptions = {},
): STTHeroPlaybackState {
  // The selected sample is the base experience; uploads temporarily override its transcript and audio URL.
  const [uncontrolledSelectedIndex, setUncontrolledSelectedIndex] = useState(0);
  const selectedIndex = controlledSelectedIndex ?? uncontrolledSelectedIndex;
  const previousControlledSelectedIndexRef = useRef(controlledSelectedIndex);

  // Sample transcripts may be server-provided, embedded on the sample, or lazily fetched by sample id.
  const { currentSample, sampleSegments, isSampleLoading, loadSegmentsIfNeeded } = useSTTHeroSampleSegments(
    samples,
    selectedIndex,
  );

  // Upload state is isolated so file and URL transcription share stale-request guards and blob cleanup.
  const {
    transcription: uploadedTranscription,
    thumbnailUrl: uploadedThumbnailUrl,
    audioUrl: uploadedAudioUrl,
    isUploading,
    fileName: uploadFileName,
    mediaKind: uploadedMediaKind,
    showQuotaExceeded: showUploadQuotaExceeded,
    resetUploadState,
    handleFileChange: handleUploadFileChange,
    handleUrlSubmit: submitUrlTranscription,
  } = useSTTHeroUpload({ uploadFn, urlTranscribeFn });

  // The active audio source follows the upload when present, otherwise it plays the selected sample.
  const audioId = `stt-hero-${currentSample?.id ?? selectedIndex}`;
  const activeAudioUrl = uploadedAudioUrl ?? currentSample?.audioUrl;
  const contentAnalyticsProps = useMemo(
    () => getSttHeroContentAnalyticsProps(uploadFileName, selectedIndex),
    [uploadFileName, selectedIndex],
  );
  const { currentTime, isPlaying, togglePlay, stop, seekToTime, resetPlayback } = useSTTHeroAudioControls({
    audioId,
    audioUrl: activeAudioUrl,
    sampleName: currentSample?.name,
    contentAnalyticsProps,
  });

  // The rendered transcript comes from the upload flow while a user upload is active.
  const segments = useMemo(() => {
    if (uploadFileName !== null) {
      return uploadedTranscription ? convertSampleResponseToSegments(uploadedTranscription) : [];
    }
    return sampleSegments;
  }, [sampleSegments, uploadFileName, uploadedTranscription]);

  const setSelectedIndex = useCallback(
    (index: number) => {
      if (controlledSelectedIndex !== undefined) {
        onSelectedIndexChange?.(index);
        return;
      }

      // Switching examples leaves upload mode and always starts from the beginning.
      resetPlayback();
      resetUploadState();
      setUncontrolledSelectedIndex(index);
      if (samples[index]) {
        loadSegmentsIfNeeded(samples[index]);
      }
    },
    [controlledSelectedIndex, onSelectedIndexChange, samples, loadSegmentsIfNeeded, resetPlayback, resetUploadState],
  );

  useEffect(() => {
    if (controlledSelectedIndex === undefined) return;
    if (previousControlledSelectedIndexRef.current === controlledSelectedIndex) return;

    previousControlledSelectedIndexRef.current = controlledSelectedIndex;
    resetPlayback();
    resetUploadState();
    if (samples[controlledSelectedIndex]) {
      loadSegmentsIfNeeded(samples[controlledSelectedIndex]);
    }
  }, [controlledSelectedIndex, samples, loadSegmentsIfNeeded, resetPlayback, resetUploadState]);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Unsupported files are ignored by the upload hook, so avoid resetting playback for them.
      if (file && MEDIA_CONTENT_TYPES.includes(file.type)) {
        resetPlayback();
      }
      void handleUploadFileChange(e);
    },
    [handleUploadFileChange, resetPlayback],
  );

  const handleUrlSubmit = useCallback(
    async (urlString: string) => {
      if (!urlString.trim()) return;

      resetPlayback();
      await submitUrlTranscription(urlString);
    },
    [resetPlayback, submitUrlTranscription],
  );

  const isLoading = uploadFileName === null && isSampleLoading;

  const reportTranscriptionDownload = useCallback(
    (download_format: string) => {
      captureEvent(EventName.mw_stt_transcription_download, {
        ...getSttHeroContentAnalyticsProps(uploadFileName, selectedIndex),
        download_format,
      });
    },
    [uploadFileName, selectedIndex],
  );

  const handleDownload = useCallback(() => {
    if (!segments.length) return;
    const text = segments.map(seg => seg.text.trim()).join("\n\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const baseName = uploadFileName ? uploadFileName.replace(/\.[^/.]+$/, "") : (currentSample?.name ?? "transcript");
    a.download = `${baseName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    reportTranscriptionDownload("txt");
  }, [segments, currentSample?.name, uploadFileName, reportTranscriptionDownload]);

  return {
    selectedIndex,
    setSelectedIndex,
    segments,
    isPlaying,
    isLoading,
    isUploading,
    uploadFileName,
    uploadedTranscription,
    uploadedThumbnailUrl,
    currentTime,
    togglePlay,
    stop,
    seekToTime,
    handleFileChange,
    handleUrlSubmit,
    handleDownload,
    reportTranscriptionDownload,
    showUploadQuotaExceeded,
    uploadedMediaKind,
  };
}
